import { useCallback, useEffect, useRef, useState } from 'react';
import type { LaunchLock } from '@repo/frontend-types';
import { getLatestLaunch } from '@repo/front-end-services';

/**
 * The Go live button's 60-second lock after a launch (spec twin-launch-history-record, R6).
 *
 * ## The clock is the server's
 *
 * The lock runs from the launch record's `launched_at`, which the server stamps. The server answers
 * `{ lockedUntil, serverNow }`, and the time left is `lockedUntil - serverNow` — a DIFFERENCE of two
 * server readings, anchored here to `performance.now()`. The browser's wall clock is never
 * consulted, so a laptop five minutes fast neither unlocks early nor locks for ever. Because the
 * record is on the server, a reload or a second person sees the same lock.
 *
 * ## Unknown is not unlocked
 *
 * A failed read — and an answer whose times do not parse — is `unknown`, and the button holds on it
 * with a *Check again*. "We could not find out when this twin was last launched" is not permission
 * to launch it again.
 *
 * ## This hook changes state on TRANSITIONS only
 *
 * It lives on the onboarding page, which renders the whole wizard. So it holds the lock's state and
 * the two instants (`endsAt`, `launchedAt`, in `performance.now()` time) and flips itself to
 * unlocked with ONE timer; the per-second countdown is drawn by the Launch step, which re-renders a
 * line of text rather than the page.
 *
 * ## Read when the Launch step opens
 *
 * `active` is true while the Launch step is showing. The lock is read each time it becomes active,
 * not once per page load, so a colleague's launch twenty minutes into a session is seen.
 */
export type LaunchLockState = 'checking' | 'unlocked' | 'locked' | 'unknown';

export interface LaunchLockView {
  state: LaunchLockState;
  /** `performance.now()` at which the lock ends; null unless `locked`. */
  endsAt: number | null;
  /** `performance.now()` at which the launch that set the lock happened; null unless `locked`. */
  launchedAt: number | null;
  /** True when the lock is held by a launch still in progress, not by the 60 s after one. */
  inProgress: boolean;
}

export interface UseLaunchLock extends LaunchLockView {
  /** Read the lock again (the *Check again* button). */
  recheck: () => void;
  /** Adopt a lock the record or claim call just returned, without another read. */
  applyLock: (lock: LaunchLock, launchedAt: string | null) => void;
}

interface Params {
  middlewareApi: string;
  accessToken: string | null;
  twinId: number | null;
  /** True while the Launch step is showing; becoming true triggers a read. */
  active: boolean;
}

/**
 * Remaining and elapsed time from two server readings, never the local clock; null when any time
 * the answer carries does not parse — which the caller treats as unknown, never as unlocked.
 */
export const lockTimes = (
  lock: LaunchLock,
  launchedAt: string | null,
): { remainingMs: number; sinceMs: number } | null => {
  const now = Date.parse(lock.serverNow);
  const until = lock.lockedUntil === null ? null : Date.parse(lock.lockedUntil);
  const at = launchedAt === null ? null : Date.parse(launchedAt);
  if (!Number.isFinite(now) || (until !== null && !Number.isFinite(until))) return null;
  if (at !== null && !Number.isFinite(at)) return null;
  return {
    remainingMs: until === null ? 0 : Math.max(0, until - now),
    sinceMs: at === null ? 0 : Math.max(0, now - at),
  };
};

const UNLOCKED: LaunchLockView = {
  state: 'unlocked',
  endsAt: null,
  launchedAt: null,
  inProgress: false,
};
const UNKNOWN: LaunchLockView = {
  state: 'unknown',
  endsAt: null,
  launchedAt: null,
  inProgress: false,
};
const CHECKING: LaunchLockView = {
  state: 'checking',
  endsAt: null,
  launchedAt: null,
  inProgress: false,
};

export const useLaunchLock = ({
  middlewareApi,
  accessToken,
  twinId,
  active,
}: Params): UseLaunchLock => {
  const [view, setView] = useState<LaunchLockView>(CHECKING);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * Which answer owns the view. A read takes a number when it starts; `applyLock` and unmount take
   * one too, so a slow read that started earlier can never overwrite a lock adopted after it.
   */
  const seqRef = useRef(0);

  const stopTimer = () => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  /** Show a lock; an answer whose times do not parse shows as unknown. */
  const show = useCallback((lock: LaunchLock, launchedAt: string | null) => {
    const times = lockTimes(lock, launchedAt);
    stopTimer();
    if (times === null) {
      setView(UNKNOWN);
      return;
    }
    if (times.remainingMs <= 0) {
      setView(UNLOCKED);
      return;
    }
    const now = performance.now();
    setView({
      state: 'locked',
      endsAt: now + times.remainingMs,
      launchedAt: now - times.sinceMs,
      inProgress: lock.inProgress === true,
    });
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setView(UNLOCKED);
    }, times.remainingMs);
  }, []);

  const applyLock = useCallback(
    (lock: LaunchLock, launchedAt: string | null) => {
      seqRef.current += 1; // any read already in flight no longer owns the view
      show(lock, launchedAt);
    },
    [show],
  );

  const read = useCallback(async (): Promise<void> => {
    if (!accessToken || twinId === null) return;
    const seq = ++seqRef.current;
    // The client never throws by contract; a throw anyway (a bad build, a broken mock) is an
    // unknown, held like any other, never an unhandled rejection that leaves the view stale. A
    // synchronous throw — a stale package build without the export — is caught too: `.catch` on the
    // call's result would never run, and the view would stay "checking" for ever.
    let outcome: Awaited<ReturnType<typeof getLatestLaunch>>;
    try {
      outcome = await getLatestLaunch(middlewareApi, accessToken, twinId);
    } catch (error) {
      outcome = {
        status: 'failed' as const,
        error: error instanceof Error ? error.message : String(error),
      };
    }
    const times =
      outcome.status === 'found'
        ? lockTimes(outcome.latest.lock, outcome.latest.launch?.launchedAt ?? null)
        : null;
    // Something newer owns the view: a later read, a lock just adopted, or an unmount.
    if (seq !== seqRef.current) return;
    if (outcome.status === 'failed' || times === null) {
      stopTimer();
      setView(UNKNOWN);
      return;
    }
    show(outcome.latest.lock, outcome.latest.launch?.launchedAt ?? null);
  }, [accessToken, middlewareApi, show, twinId]);

  const recheck = useCallback(() => {
    stopTimer();
    setView(CHECKING);
    void read();
  }, [read]);

  // A different twin, or no token, starts again from "checking" — never showing the last twin's lock.
  useEffect(() => {
    seqRef.current += 1;
    stopTimer();
    setView(CHECKING);
  }, [twinId, accessToken]);

  // Read each time the Launch step opens (and when the twin or token changes while it is open).
  useEffect(() => {
    if (!active || !accessToken || twinId === null) return;
    void read();
  }, [active, accessToken, twinId, read]);

  useEffect(
    () => () => {
      seqRef.current += 1; // a read resolving after unmount must not restart a timer
      stopTimer();
    },
    [],
  );

  return { ...view, recheck, applyLock };
};
